import { type ReactNode, useEffect } from 'react';
import { isSentryInitialized } from '../sentry';
import { addLifecycleBreadcrumb } from '../utils/breadcrumbs';
import { clearUser, createUserContext, setUser } from '../utils/userContext';

/**
 * Props for ObservabilityProvider
 */
interface ObservabilityProviderProps {
  children: ReactNode;
  /**
   * Current authenticated user (optional)
   * Pass the user object from @commander/auth
   */
  user?: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    company_employee?: {
      company_id?: string;
      company?: {
        id?: string;
        name?: string;
      };
    } | null;
  } | null;
  /**
   * Whether the user is authenticated
   */
  isAuthenticated?: boolean;
}

/**
 * ObservabilityProvider - Manages user context for error tracking
 *
 * This provider automatically syncs the authenticated user with Sentry
 * so all errors are associated with the correct user.
 *
 * Usage:
 * ```tsx
 * import { ObservabilityProvider } from '@commander/observability';
 * import { useAuth } from '@commander/auth';
 *
 * function App() {
 *   const { user, isAuthenticated } = useAuth();
 *
 *   return (
 *     <ObservabilityProvider user={user} isAuthenticated={isAuthenticated}>
 *       <YourApp />
 *     </ObservabilityProvider>
 *   );
 * }
 * ```
 */
export function ObservabilityProvider({
  children,
  user,
  isAuthenticated = false,
}: ObservabilityProviderProps) {
  // Sync user context with Sentry when auth state changes
  useEffect(() => {
    if (!isSentryInitialized()) return;

    if (isAuthenticated && user) {
      const userContext = createUserContext(user);
      setUser(userContext);
      addLifecycleBreadcrumb('update', 'ObservabilityProvider', {
        event: 'user-set',
        userId: user.id,
      });
    } else {
      clearUser();
      addLifecycleBreadcrumb('update', 'ObservabilityProvider', {
        event: 'user-cleared',
      });
    }
  }, [isAuthenticated, user]);

  // Track provider mount/unmount
  useEffect(() => {
    addLifecycleBreadcrumb('mount', 'ObservabilityProvider');

    return () => {
      addLifecycleBreadcrumb('unmount', 'ObservabilityProvider');
    };
  }, []);

  return <>{children}</>;
}
